/**
 * /admin/dashboard — operator landing page.
 *
 * Numeric cards only — no chart library. Polls every 10s. Highlights
 * abnormal conditions (banned accounts, stream backlog, untouched leads).
 */

import { useQuery } from '@tanstack/react-query';
import { type DashboardOverview, Routes } from '@tg-leads/contracts';

import { api } from '../../lib/api';

export function DashboardPage() {
  const ov = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardOverview>(Routes.admin.dashboard),
    refetchInterval: 10_000,
  });

  if (ov.isLoading) {
    return <div className="text-slate-500 text-sm">加载中…</div>;
  }
  if (!ov.data) {
    return <div className="text-red-600 text-sm">加载失败</div>;
  }
  const d = ov.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">运营总览</h1>
        <p className="text-sm text-slate-500 mt-1">
          每 10 秒自动刷新 · 服务器时间 {new Date(d.serverNow).toLocaleString()}
        </p>
      </div>

      {/* TG 账号 */}
      <Section title="TG 账号">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="账号总数" value={d.accounts.total} />
          <Card
            label="已上线（mtcute 已连接）"
            value={d.accounts.connected}
            tone={
              d.accounts.connected < (d.accounts.byStatus.active ?? 0)
                ? 'warn'
                : 'ok'
            }
          />
          <Card label="启用中" value={d.accounts.byStatus.active ?? 0} tone="ok" />
          <Card
            label="已封禁"
            value={d.accounts.byStatus.banned ?? 0}
            tone={(d.accounts.byStatus.banned ?? 0) > 0 ? 'fail' : 'neutral'}
          />
          <Card label="受限中" value={d.accounts.byStatus.restricted ?? 0} />
          <Card label="冷却中" value={d.accounts.byStatus.cooling ?? 0} />
          <Card
            label="采集号"
            value={d.accounts.byPurpose.observer}
            hint="只加群、不发私信"
          />
          <Card
            label="外联号"
            value={d.accounts.byPurpose.sender}
            tone={d.accounts.byPurpose.sender === 0 ? 'warn' : 'ok'}
            hint={
              d.accounts.byPurpose.sender === 0
                ? '没有外联号 — 自动外联永远跑不起来'
                : '只发私信、不加群'
            }
          />
        </div>
      </Section>

      {/* 群 */}
      <Section title="群组">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="群组总数" value={d.groups.total} />
          <Card label="已加入" value={d.groups.active} tone="ok" />
        </div>
      </Section>

      {/* Ingest */}
      <Section title="消息采集与管线">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card
            label="近 1 小时入库消息"
            value={d.ingest.rawMessagesLast1h}
          />
          <Card
            label="近 24 小时入库消息"
            value={d.ingest.rawMessagesLast24h}
          />
          <Card label="累计入库消息" value={d.ingest.rawMessagesTotal} />
          <Card
            label="Redis 流长度（历史累计）"
            value={d.ingest.streamLength}
            tone={d.ingest.streamLength > 80_000 ? 'warn' : 'neutral'}
            hint={
              d.ingest.streamLength > 80_000
                ? '接近 10 万软上限，老事件即将被自动裁掉'
                : 'XACK 后不自动 trim，只增不减；积压看下一项'
            }
          />
          <Card
            label="未确认（管线积压）"
            value={d.ingest.streamPending}
            tone={d.ingest.streamPending > 100 ? 'warn' : 'neutral'}
            hint={
              d.ingest.streamPending > 100
                ? '事件堆积或反复失败，管线消费跟不上'
                : ''
            }
          />
        </div>
      </Section>

      {/* Leads */}
      <Section title="线索">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card label="线索总数" value={d.leads.total} />
          <Card label="今日新增" value={d.leads.today} tone="ok" />
          <Card label="近 24 小时" value={d.leads.last24h} />
          <Card
            label="待认领"
            value={d.leads.byStatus.detected ?? 0}
            tone={(d.leads.byStatus.detected ?? 0) > 0 ? 'warn' : 'neutral'}
            hint={
              (d.leads.byStatus.detected ?? 0) > 0 ? '销售员有线索可认领' : ''
            }
          />
          <Card
            label="跟进中"
            value={d.leads.byStatus.assigned ?? 0}
            tone="ok"
          />
          <Card label="已成单" value={d.leads.byStatus.closed_won ?? 0} tone="ok" />
          <Card label="未成单" value={d.leads.byStatus.closed_lost ?? 0} />
        </div>
      </Section>

      {/* Outbound */}
      <Section title="自动外联">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card
            label="待发送队列"
            value={d.outbound.queued}
            tone={d.outbound.queued > 50 ? 'warn' : 'neutral'}
            hint={
              d.outbound.queued > 50
                ? '调度器没消化掉，可能账号配额满或在静音时段'
                : '等下一次调度（每 30 秒一次）'
            }
          />
          <Card
            label="已发送等回复"
            value={d.outbound.inFlight}
            hint="发出私信后等回复；7 天未回退回抢单池"
          />
          <Card
            label="对方已回复"
            value={d.outbound.replied}
            tone={d.outbound.replied > 0 ? 'ok' : 'neutral'}
            hint={d.outbound.replied > 0 ? '等销售员认领接管' : ''}
          />
          <Card
            label="近 24h 发送 / 回复"
            value={`${d.outbound.sentLast24h} / ${d.outbound.repliesLast24h}`}
            hint={`回复率 ${(d.outbound.replyRate24h * 100).toFixed(1)}%`}
            tone={
              d.outbound.sentLast24h > 0 && d.outbound.replyRate24h < 0.02
                ? 'warn'
                : 'neutral'
            }
          />
        </div>
      </Section>

      {/* LLM */}
      <Section title="模型调用">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="今日调用次数" value={d.llm.today.calls} />
          <Card label="今日 token 总数" value={d.llm.today.totalTokens} />
          <Card
            label="今日成本（美元）"
            value={`$${d.llm.today.costUsd.toFixed(4)}`}
          />
          <Card label="近 24 小时调用次数" value={d.llm.last24h.calls} />
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

type Tone = 'ok' | 'warn' | 'fail' | 'neutral';

function Card({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: string | number;
  tone?: Tone;
  hint?: string;
}) {
  const toneClass: Record<Tone, string> = {
    ok: 'border-l-4 border-emerald-500',
    warn: 'border-l-4 border-orange-500',
    fail: 'border-l-4 border-red-500',
    neutral: 'border-l-4 border-slate-300',
  };
  return (
    <div
      className={`card ${toneClass[tone]} px-4 py-3`}
      title={hint ?? undefined}
    >
      <div className="text-xs text-slate-500 truncate">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      {hint && (
        <div className="text-[10px] text-slate-400 mt-1">{hint}</div>
      )}
    </div>
  );
}
